/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { logError } from '../utils/logger';

export interface Notification {
  _id: string;
  userId: string;
  role: string;
  title: string;
  message: string;
  type?: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface NotificationsResponse {
  success: boolean;
  data: Notification[];
  total?: number;
  unreadCount?: number;
  pagination: PaginationInfo;
}

export interface NotificationActionResponse {
  success: boolean;
  message?: string;
  data?: any;
}

/**
 * Get all notifications for a user
 * GET /api/:userId/:role
 */
export const getNotifications = async (
  userId: string,
  role: string,
  userCreatedAt?: string,
  filters?: {
    type?: string;
    priority?: string;
    readStatus?: string;
    sortBy?: string;
    search?: string;
    limit?: number;
    page?: number;
  }
): Promise<NotificationsResponse> => {
  try {
    let url =
      `${import.meta.env.VITE_APP_BACKEND_PORT}/${Path.getNotifications}`
        .replace(':userId', userId)
        .replace(':role', role);

    const params = new URLSearchParams();

    if (userCreatedAt) {
      params.append('userCreatedAt', userCreatedAt);
    }
    if (filters?.type && filters.type !== '') {
      params.append('type', filters.type);
    }
    if (filters?.priority && filters.priority !== '') {
      params.append('priority', filters.priority);
    }
    if (filters?.readStatus && filters.readStatus !== '') {
      params.append('readStatus', filters.readStatus);
    }
    if (filters?.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters?.search && filters.search.trim() !== '') {
      params.append('search', filters.search.trim());
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching notifications:', error);
    logError('Error fetching notifications:', { error });
    throw error;
  }
};

/**
 * Get unread notifications for a user
 * GET /api/getunread/:userId/:role
 */
export const getUnreadNotifications = async (
  userId: string,
  role: string,
  userCreatedAt?: string
): Promise<NotificationsResponse> => {
  try {
    let url =
      `${import.meta.env.VITE_APP_BACKEND_PORT}/${Path.getUnreadNotifications}`
        .replace(':userId', userId)
        .replace(':role', role);

    if (userCreatedAt) {
      url += `?userCreatedAt=${encodeURIComponent(userCreatedAt)}`;
    }

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching unread notifications:', error);
    logError('Error fetching unread notifications:', { error });
    throw error;
  }
};

export const markNotificationAsRead = async (
  userId: string,
  role: string,
  notificationIds: string[]
): Promise<NotificationActionResponse> => {
  try {
    const response = await apiClient.patch(
      `${import.meta.env.VITE_APP_BACKEND_PORT}/${Path.markNotificationAsRead}/${userId}/${role}`,
      notificationIds
    );

    return response.data;
  } catch (error: any) {
    console.error('Error marking notification as read:', error);
    logError('Error marking notification as read:', { error });
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * PATCH /api/mark-all/:userId/:role
 */
export const markAllNotificationsAsRead = async (
  userId: string,
  role: string
): Promise<NotificationActionResponse> => {
  try {
    const url =
      `${import.meta.env.VITE_APP_BACKEND_PORT}/${Path.markAllNotificationsAsRead}`
        .replace(':userId', userId)
        .replace(':role', role);

    const response = await apiClient.patch(url);
    return response.data;
  } catch (error: any) {
    console.error('Error marking all notifications as read:', error);
    logError('Error marking all notifications as read:', { error });
    throw error;
  }
};

/**
 * Delete a notification
 * DELETE /api/:userId
 */
export const deleteNotification = async (
  userId: string,
  notificationId: string
): Promise<NotificationActionResponse> => {
  try {
    const url =
      `${import.meta.env.VITE_APP_BACKEND_PORT}/${Path.deleteNotification}`.replace(
        ':userId',
        userId
      );

    const response = await apiClient.delete(url, {
      data: { notificationId },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error deleting notification:', error);
    logError('Error deleting notification:', { error });
    throw error;
  }
};
